import { useEffect, useRef, useState } from "react";

// ============================================================================
//  Tótem "Recuperá tu entrada" — NAUFest Uruguay 2026
//  Mismo estilo que el formulario de inscripción (fondo gris, barra cian,
//  logo, tarjeta blanca, Montserrat, footer).
//
//  · QR con api.qrserver.com (el MISMO servicio del mail; sin dependencias).
//  · Descarga: botón que baja la entrada en PNG + aviso de captura.
//  · Muestra las mesas de mentoreo asignadas (solo las que tengan valor).
//  · Si el documento no está en la lista → botón "Inscribite" (INSCRIPCION_URL).
//  · Los datos se consultan por documento al Apps Script, que devuelve SOLO
//    ese registro. Pegá la URL de tu implementación en ENDPOINT.
// ============================================================================

const LOGO_URL = "https://naufest.com/wp-content/uploads/2026/06/LOGO-NAUFEST-mas-grande.png";

// URL de tu Apps Script (termina en /exec)
const ENDPOINT = "https://script.google.com/macros/s/AKfycby04YuRGJJrIMMg282gEeTv4yqjJUuOnLL2Ye_mub1A8wZphVjNge2yFCn3U5dGPuMs/exec";
// A dónde se manda a la persona que todavía no se inscribió
const INSCRIPCION_URL = "https://witty-coast-0bdf2220f.6.azurestaticapps.net/naufest";

// Genera la URL del QR con el mismo servicio que usás en el mail.
const qrURL = (data, size) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(data)}`;

// Consulta JSONP al Apps Script (evita problemas de CORS desde el navegador).
function consultarDocumento(doc) {
  return new Promise((resolve, reject) => {
    const cb = "cb_" + Math.random().toString(36).slice(2);
    const s = document.createElement("script");
    const limpiar = () => { delete window[cb]; if (s.parentNode) s.parentNode.removeChild(s); };
    window[cb] = (data) => { limpiar(); resolve(data); };
    s.onerror = () => { limpiar(); reject(new Error("network")); };
    s.src = ENDPOINT + "?doc=" + encodeURIComponent(doc) + "&callback=" + cb;
    document.body.appendChild(s);
    setTimeout(() => { if (window[cb]) { limpiar(); reject(new Error("timeout")); } }, 12000);
  });
}

const docKey = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

export default function TotemEntrada() {
  const [doc, setDoc] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [noEncontrada, setNoEncontrada] = useState(false);
  const [cargando, setCargando] = useState(false);
  const inputRef = useRef(null);

  const volver = () => { setData(null); setDoc(""); setError(""); setNoEncontrada(false); };

  useEffect(() => { if (!data && inputRef.current) inputRef.current.focus(); }, [data]);

  const buscar = async () => {
    if (cargando) return;
    setError(""); setNoEncontrada(false);
    if (docKey(doc).length < 5) { setError("Revisá el número de documento."); return; }
    setCargando(true);
    try {
      const res = await consultarDocumento(doc.trim());
      if (res && res.found) setData(res);
      else setNoEncontrada(true);
    } catch (e) {
      setError("No pudimos conectar. Verificá la conexión e intentá de nuevo.");
    } finally {
      setCargando(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.accentBar} />

      <div style={S.logoWrap}>
        <img src={LOGO_URL} alt="NAUFest Logo" style={S.logo} />
      </div>

      <div style={S.card}>
        {data ? (
          <EntradaCard data={data} onVolver={volver} />
        ) : (
          <>
            <h1 style={S.title}>Recuperá tu entrada</h1>
            <p style={S.subtitle}>
              ¿No encontrás tu entrada en el correo? Ingresá tu número de documento y
              descargala desde acá.
            </p>

            <div style={S.group}>
              <label style={S.label}>Documento<span style={S.req}> *</span></label>
              <input
                ref={inputRef}
                style={{ ...S.input, ...S.inputDoc }}
                type="text"
                inputMode="numeric"
                value={doc}
                onChange={(e) => { setDoc(e.target.value); setError(""); setNoEncontrada(false); }}
                onKeyDown={(e) => e.key === "Enter" && buscar()}
                placeholder="Sin puntos ni guiones"
                autoComplete="off"
                spellCheck="false"
                disabled={cargando}
              />
            </div>

            {error && <div style={S.error}>{error}</div>}

            {noEncontrada ? (
              <div style={S.notFound}>
                <p style={S.notFoundText}>
                  No encontramos una entrada con ese documento. Si todavía no te inscribiste,
                  podés hacerlo ahora.
                </p>
                <a href={INSCRIPCION_URL} style={S.inscribiteBtn}>Inscribite</a>
                <button type="button" style={S.linkBtn} onClick={() => setNoEncontrada(false)}>
                  Probar con otro documento
                </button>
              </div>
            ) : (
              <button
                type="button"
                style={{ ...S.submit, ...(cargando ? S.submitOff : {}) }}
                onClick={buscar}
                disabled={cargando}
              >
                {cargando ? "Buscando…" : "Ver mi entrada"}
              </button>
            )}
          </>
        )}
      </div>

      <footer style={S.footer}>
        © 2026 DESEM Junior Achievement Uruguay · Todos los derechos reservados
      </footer>
    </div>
  );
}

function EntradaCard({ data, onVolver }) {
  const [bajando, setBajando] = useState(false);
  const nombre = `${data.nombre || ""} ${data.apellido || ""}`.trim();
  const mesas = Array.isArray(data.mesas) ? data.mesas : [];

  // Auto-volver a la búsqueda tras 45s (pensado para el tótem).
  useEffect(() => {
    const t = setTimeout(onVolver, 45000);
    return () => clearTimeout(t);
  }, [onVolver]);

  const descargar = async () => {
    if (bajando) return;
    setBajando(true);
    const hi = qrURL(data.qr, 800); // versión de alta resolución para guardar
    try {
      const resp = await fetch(hi);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `entrada-naufest-${docKey(nombre) || "qr"}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      window.open(hi, "_blank"); // fallback: abrir la imagen para guardarla a mano
    } finally {
      setBajando(false);
    }
  };

  return (
    <div style={S.ticket}>
      <span style={S.chip}>EDICIÓN 2026</span>
      <p style={S.ticketMsg}>Presentá este código al ingresar</p>

      <div style={S.qrBox}>
        <img src={qrURL(data.qr, 232)} alt="Código QR de tu entrada" width={232} height={232} style={S.qrImg} />
      </div>

      <div style={S.divider} />

      <div style={S.name}>{nombre}</div>
      <span style={S.rolePill}>PARTICIPANTE</span>

      {mesas.length > 0 && (
        <div style={S.mesas}>
          <div style={S.mesasTitle}>Tus mesas de mentoreo</div>
          {mesas.map((m, i) => (
            <div key={i} style={{ ...S.mesaItem, ...(i === mesas.length - 1 ? S.mesaItemLast : {}) }}>
              <span style={S.mesaNum}>{i+1}</span>
              <span style={S.mesaNum}>Mesa {m.numero}</span>
              {m.tematica ? <span style={S.mesaTema}>{m.tematica}</span> : null}
            </div>
          ))}
        </div>
      )}

      <button type="button" style={{ ...S.submit, ...(bajando ? S.submitOff : {}) }} onClick={descargar} disabled={bajando}>
        {bajando ? "Descargando…" : "Descargar entrada (PNG)"}
      </button>
      <p style={S.captura}>📸 También podés sacar una captura de pantalla.</p>

      <button type="button" style={S.btnGhost} onClick={onVolver}>Listo, cerrar</button>
    </div>
  );
}

const CYAN = "#00BCD4";
const S = {
  page: {
    fontFamily: "'Montserrat',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    background: "#EFEFEF", minHeight: "100vh", color: "#1E293B",
    display: "flex", flexDirection: "column",
  },
  accentBar: { width: "100%", height: 12, background: CYAN },
  logoWrap: { maxWidth: 1100, margin: "2rem auto 0", padding: "0 1.5rem", textAlign: "center" },
  logo: { maxWidth: 220, height: "auto" },

  card: {
    maxWidth: 560, width: "100%", margin: "1.5rem auto", background: "#fff",
    borderRadius: 16, padding: "2rem", boxShadow: "0 10px 30px rgba(0,0,0,.06)",
    boxSizing: "border-box", flex: "0 0 auto",
  },
  title: { fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.25 },
  subtitle: { margin: "0.75rem 0 1.5rem", color: "#475569", fontSize: 14, lineHeight: 1.6 },

  group: { marginBottom: "1rem" },
  label: { display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#334155" },
  req: { color: CYAN },
  input: {
    width: "100%", padding: "11px 12px", fontSize: 14, borderRadius: 10,
    border: "1px solid #CBD5E1", outline: "none", fontFamily: "inherit",
    background: "#fff", color: "#1E293B", boxSizing: "border-box",
  },
  inputDoc: { textAlign: "center", fontSize: 20, fontWeight: 600, letterSpacing: 1, padding: "16px 12px" },

  error: {
    margin: "1rem 0 0", padding: "0.75rem 1rem", borderRadius: 10,
    background: "#FEF2F2", border: "1px solid #FECACA", color: "#DC2626", fontSize: 14,
  },

  // ---- No encontrada → invitar a inscribirse ----
  notFound: {
    marginTop: "1.25rem", padding: "1.25rem", borderRadius: 12,
    background: "#F0FDFF", border: "1px solid #A5E8F0", textAlign: "center",
    display: "flex", flexDirection: "column", alignItems: "center",
  },
  notFoundText: { margin: "0 0 1rem", fontSize: 14, color: "#334155", lineHeight: 1.55 },
  inscribiteBtn: {
    display: "inline-block", padding: "13px 34px", borderRadius: 12,
    background: CYAN, color: "#fff", fontSize: 16, fontWeight: 800,
    textDecoration: "none", fontFamily: "inherit", letterSpacing: "0.01em",
  },
  linkBtn: {
    marginTop: "0.85rem", background: "none", border: "none", color: "#0E7490",
    fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
    textDecoration: "underline", textUnderlineOffset: 2,
  },

  submit: {
    width: "100%", marginTop: "1.5rem", padding: "14px", borderRadius: 12, border: "none",
    background: CYAN, color: "#fff", fontSize: 16, fontWeight: 800, cursor: "pointer",
    fontFamily: "inherit", letterSpacing: "0.01em",
  },
  submitOff: { background: "#CBD5E1", cursor: "not-allowed" },

  // ---- Entrada / ticket ----
  ticket: { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" },
  chip: {
    display: "inline-block", background: "#E0F7FA", color: "#00838F", fontSize: 13,
    fontWeight: 700, letterSpacing: 2, padding: "8px 20px", borderRadius: 6,
  },
  ticketMsg: { margin: "0.9rem 0 1.25rem", color: "#475569", fontSize: 14, fontWeight: 500 },
  qrBox: { border: "1px solid #E3EEF0", borderRadius: 16, padding: 14, background: "#fff", lineHeight: 0 },
  qrImg: { display: "block", width: 232, height: 232 },
  divider: { width: 230, maxWidth: "100%", margin: "1.4rem auto 1rem", borderTop: "3px dotted #CBD5E1" },
  name: { color: "#0F172A", fontSize: 24, fontWeight: 800, paddingBottom: 14 },
  rolePill: {
    display: "inline-block", background: "#111827", color: "#fff", fontSize: 14,
    fontWeight: 700, letterSpacing: 1, padding: "9px 28px", borderRadius: 999,
  },

  // ---- Mesas de mentoreo ----
  mesas: {
    width: "100%", marginTop: "1.5rem", padding: "1rem 1.25rem", boxSizing: "border-box",
    background: "#F0FDFF", border: "1px solid #A5E8F0", borderRadius: 12, textAlign: "left",
  },
  mesasTitle: {
    fontSize: 12, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase",
    color: "#0E7490", marginBottom: "0.75rem",
  },
  mesaItem: {
    display: "flex", alignItems: "center", gap: 12, padding: "0.6rem 0",
    borderBottom: "1px solid #D4EEF3",
  },
  mesaItemLast: { borderBottom: "none", paddingBottom: 0 },
  mesaNum: {
    flex: "0 0 auto", background: "#00838F", color: "#fff", fontSize: 13, fontWeight: 800,
    padding: "6px 14px", borderRadius: 999, whiteSpace: "nowrap",
  },
  mesaTema: { fontSize: 14, fontWeight: 600, color: "#1E293B", lineHeight: 1.35 },

  captura: { margin: "0.75rem 0 0", fontSize: 13, color: "#64748B", fontWeight: 500 },
  btnGhost: {
    marginTop: "1.25rem", padding: "12px 26px", borderRadius: 12,
    border: "1px solid #CBD5E1", background: "#fff", color: "#334155",
    fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
  },

  footer: { textAlign: "center", padding: "1.5rem", fontSize: 12, color: "#94A3B8", marginTop: "auto" },
};